import { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { getConfiguracionDashboard, getCatalogoFiltro, getIndicadoresDashboard } from '../api/dashboardApi';
import { AuthContext } from '../context/AuthContext';

const EMPTY_DATASET = {
  demografia: [],
  gruposRiesgo: [],
  actividadesGruposEdades: [],
  actividadesCohorte: [],
};

const normalizeRows = (arr) => (arr || []).map((item) => {
  const normalized = {};
  Object.entries(item).forEach(([k, v]) => {
    const lowerK = String(k).toLowerCase();
    if (
      typeof v === 'string'
      && v.trim() !== ''
      && !Number.isNaN(Number(v))
    ) {
      normalized[lowerK] = Number(v);
    } else {
      normalized[lowerK] = v;
    }
  });
  return normalized;
});

const useDashboardData = () => {
  const { sedeActiva } = useContext(AuthContext);

  const [config, setConfig] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [filtrosValores, setFiltrosValores] = useState({});
  const [catalogos, setCatalogos] = useState({});
  const [indicadoresData, setIndicadoresData] = useState({ ...EMPTY_DATASET });
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingIndicadores, setLoadingIndicadores] = useState(false);
  const [error, setError] = useState(null);

  const fetchCounterRef = useRef(0);
  const prevCatalogoParamsRef = useRef({});
  const initialFetchRef = useRef(false);
  const cacheByTabRef = useRef({});
  const configRef = useRef([]);
  const filtrosValoresRef = useRef({});
  const activeTabRef = useRef(null);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    filtrosValoresRef.current = filtrosValores;
  }, [filtrosValores]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const buildFiltrosForSeccion = useCallback((tabClave) => {
    const seccion = (configRef.current || []).find((s) => s.clave_tecnica === tabClave);
    const filtrosSeccion = seccion?.filtros || [];
    const out = {};
    const valoresAcumuladosPorCampo = {};
    filtrosSeccion.forEach((f) => {
      const readKey = (f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin')
        ? f.nombre_campo
        : f.clave_vinculacion;
      const rawValor = filtrosValoresRef.current[readKey];
      const valorString = Array.isArray(rawValor) ? rawValor.join(',') : (rawValor || '');
      if (!valorString) return;
      const bucket = valoresAcumuladosPorCampo[f.nombre_campo]
        || (valoresAcumuladosPorCampo[f.nombre_campo] = []);
      valorString.split(',').forEach((v) => {
        const t = v.trim();
        if (t) bucket.push(t);
      });
    });
    Object.entries(valoresAcumuladosPorCampo).forEach(([campo, valores]) => {
      const unicos = Array.from(new Set(valores));
      out[campo] = unicos.join(',');
    });
    return out;
  }, []);

  const fingerprintFor = useCallback((tabClave) => {
    const filtros = buildFiltrosForSeccion(tabClave);
    return JSON.stringify(filtros);
  }, [buildFiltrosForSeccion]);

  const cargarIndicadoresSeccion = useCallback(async (tabClave) => {
    if (!tabClave) return;

    const fingerprint = fingerprintFor(tabClave);
    const cached = cacheByTabRef.current[tabClave];
    if (cached && cached.fingerprint === fingerprint) {
      setIndicadoresData((prev) => ({
        ...prev,
        ...cached.payload,
      }));
      return;
    }

    const filtros = buildFiltrosForSeccion(tabClave);
    const myFetchId = ++fetchCounterRef.current;

    setLoadingIndicadores(true);
    try {
      const response = await getIndicadoresDashboard(filtros, tabClave);
      const res = response?.data || response || {};

      if (myFetchId !== fetchCounterRef.current) return;

      const partial = {
        demografia: normalizeRows(res.demografia),
        gruposRiesgo: normalizeRows(res.gruposRiesgo),
        actividadesGruposEdades: normalizeRows(res.actividadesGruposEdades),
        actividadesCohorte: normalizeRows(res.actividadesCohorte),
      };

      cacheByTabRef.current[tabClave] = {
        fingerprint,
        payload: partial,
      };

      setIndicadoresData((prev) => ({
        ...prev,
        ...partial,
      }));
    } catch (err) {
      console.error(err);
      setError('Error al cargar indicadores');
    } finally {
      if (myFetchId === fetchCounterRef.current) {
        setLoadingIndicadores(false);
      }
    }
  }, [buildFiltrosForSeccion, fingerprintFor]);

  const cargarCatalogosDeTab = useCallback(async (tabClave) => {
    const seccion = configRef.current.find((s) => s.clave_tecnica === tabClave);
    if (!seccion) return;

    let catCargados = 0;
    const nuevosDefaults = {};
    const nuevosCatalogos = {};
    let catalogoCambiado = false;

    for (const filtro of seccion.filtros) {
      if (!filtro.query_catalogo) {
        if (filtro.nombre_campo.toLowerCase() === 'sexo') {
          if (!catalogos[filtro.id]) {
            nuevosCatalogos[filtro.id] = [
              { id: 'M', descripcion: 'Masculino' },
              { id: 'F', descripcion: 'Femenino' },
            ];
            catalogoCambiado = true;
          }
        }
        catCargados++;
        continue;
      }

      try {
        const params = {};
        if (filtro.id_filtro_padre) {
          const padre = seccion.filtros.find((f) => f.id === filtro.id_filtro_padre);
          if (padre) {
            const readKeyPadre = (padre.nombre_campo === 'edadIni' || padre.nombre_campo === 'edadFin')
              ? padre.nombre_campo
              : padre.clave_vinculacion;
            const valorPadre = filtrosValoresRef.current[readKeyPadre];
            if (valorPadre) {
              params[padre.nombre_campo] = Array.isArray(valorPadre) ? valorPadre.join(',') : valorPadre;
            } else {
              continue;
            }
          }
        }

        const paramsString = JSON.stringify(params);
        if (prevCatalogoParamsRef.current[filtro.id] === paramsString) {
          catCargados++;
          continue;
        }
        prevCatalogoParamsRef.current[filtro.id] = paramsString;

        const res = await getCatalogoFiltro(filtro.id, params);
        const nuevaData = res.data || [];

        const isMarcarActivo = filtro.marcar_x_defecto === true
          || filtro.marcar_x_defecto === 1
          || String(filtro.marcar_x_defecto) === '1';

        if (filtro.tipo_control === 'select_multiple'
          && isMarcarActivo
          && filtrosValoresRef.current[filtro.clave_vinculacion] === undefined) {
          let valorDefault;
          const isSedeGlobal = String(filtro.clave_vinculacion || '').toUpperCase() === 'SEDE_GLOBAL'
            || String(filtro.nombre_campo || '').toLowerCase() === 'sede';
          if (isSedeGlobal && sedeActiva?.id != null) {
            const idSedeActiva = String(sedeActiva.id);
            const existeEnCatalogo = nuevaData.some((item) => String(item.id) === idSedeActiva);
            valorDefault = existeEnCatalogo ? [idSedeActiva] : [];
          } else {
            valorDefault = nuevaData
              .map((item) => String(item.id || item.eps || item.codigo))
              .filter(Boolean);
          }
          if (valorDefault.length > 0) {
            nuevosDefaults[filtro.clave_vinculacion] = valorDefault;
          }
        }

        const prevStr = JSON.stringify(catalogos[filtro.id]);
        const nextStr = JSON.stringify(nuevaData);
        if (prevStr !== nextStr) {
          nuevosCatalogos[filtro.id] = nuevaData;
          catalogoCambiado = true;
        }
        catCargados++;
      } catch (err) {
        console.warn(`Error en catálogo ${filtro.label}`, err);
      }
    }

    if (Object.keys(nuevosDefaults).length > 0) {
      setFiltrosValores((prev) => {
        const merged = { ...prev };
        Object.entries(nuevosDefaults).forEach(([k, v]) => {
          if (merged[k] === undefined) merged[k] = v;
        });
        filtrosValoresRef.current = merged;
        return merged;
      });
    }

    if (catalogoCambiado) {
      setCatalogos((prev) => {
        const merged = { ...prev, ...nuevosCatalogos };
        return merged;
      });
    }

    if (catCargados === seccion.filtros.length && !initialFetchRef.current) {
      const filtroSede = seccion.filtros.find((f) => f.nombre_campo === 'sede');
      const sedeLista = !filtroSede || !!filtrosValoresRef.current[filtroSede.clave_vinculacion];
      if (!sedeLista) return;
      initialFetchRef.current = true;
      setTimeout(() => {
        cargarIndicadoresSeccion(tabClave);
      }, 0);
    }
  }, [catalogos, cargarIndicadoresSeccion]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await getConfiguracionDashboard();
        const data = res.data || res;
        setConfig(data);
        configRef.current = data;
        if (data.length > 0) {
          const primera = data[0].clave_tecnica;
          setActiveTab(primera);
          activeTabRef.current = primera;
        }
      } catch (err) {
        setError('No se pudo cargar la configuración del dashboard');
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (!sedeActiva?.id) return;
    const allFiltros = (configRef.current || []).flatMap((s) => s.filtros || []);
    const filtroSede = allFiltros.find((f) =>
      String(f.nombre_campo || '').toLowerCase() === 'sede'
      || String(f.clave_vinculacion || '').toUpperCase() === 'SEDE_GLOBAL'
    );
    if (!filtroSede) return;
    const keySede = filtroSede.clave_vinculacion;
    if (!filtrosValoresRef.current[keySede]) {
      const sedeId = String(sedeActiva.id);
      setFiltrosValores((prev) => {
        const next = { ...prev, [keySede]: [sedeId] };
        filtrosValoresRef.current = next;
        return next;
      });
    }
  }, [sedeActiva]);

  useEffect(() => {
    if (!activeTab || loadingConfig) return;
    cargarCatalogosDeTab(activeTab);
  }, [activeTab, loadingConfig, cargarCatalogosDeTab]);

  useEffect(() => {
    if (!activeTab || loadingConfig) return;
    cargarCatalogosDeTab(activeTab);
  }, [filtrosValores, activeTab, loadingConfig, cargarCatalogosDeTab]);

  const handleSetActiveTab = useCallback((clave) => {
    setActiveTab(clave);
    activeTabRef.current = clave;
    if (clave) {
      cargarIndicadoresSeccion(clave);
    }
  }, [cargarIndicadoresSeccion]);

  const handleFiltroChange = useCallback((key, val) => {
    setFiltrosValores((prev) => {
      const normalizedVal = Array.isArray(val)
        ? val.map((v) => String(v))
        : val;
      const next = { ...prev, [key]: normalizedVal };
      filtrosValoresRef.current = next;
      return next;
    });
  }, []);

  const aplicarFiltros = useCallback(() => {
    const currentTab = activeTabRef.current;
    if (!currentTab) return;
    cacheByTabRef.current[currentTab] = undefined;
    cargarIndicadoresSeccion(currentTab);
  }, [cargarIndicadoresSeccion]);

  const limpiarFiltros = useCallback(() => {
    setFiltrosValores({});
    filtrosValoresRef.current = {};
    cacheByTabRef.current = {};
    prevCatalogoParamsRef.current = {};
    initialFetchRef.current = false;
  }, []);

  const gruposEdadesChartData = useMemo(() => {
    const map = new Map();
    const dataset = indicadoresData.demografia || [];

    dataset.forEach((item) => {
      const g = item.grupo || 'Desconocido';
      if (!map.has(g)) map.set(g, { name: g, M: 0, F: 0, Total: 0 });

      const cant = Number(item.cantidad || 0);
      const entry = map.get(g);

      const sexo = String(item.sexo || '').toUpperCase();
      if (sexo === 'M') entry.M += cant;
      else if (sexo === 'F') entry.F += cant;

      entry.Total = entry.M + entry.F;
    });

    return Array.from(map.values());
  }, [indicadoresData]);

  const gruposRiesgoData = useMemo(() => {
    const map = {};
    (indicadoresData.gruposRiesgo || []).forEach((item) => {
      const gId = item.idgrupo;
      if (!map[gId]) {
        map[gId] = { id: gId, name: item.grupo, total: 0, subgrupos: [] };
      }
      const subGrupo = String(item.subgrupo || '');
      const pacientes = Number(item.numeropacientes || 0);
      if (subGrupo === 'TOTAL_GRUPO') {
        map[gId].total = pacientes;
      } else if (subGrupo) {
        map[gId].subgrupos.push({ name: subGrupo, value: pacientes });
      }
    });
    return Object.values(map);
  }, [indicadoresData]);

  const processActividades = (dataset) => {
    const map = {};
    (dataset || []).forEach((item) => {
      const act = item.actividad || 'Desconocido';
      if (!map[act]) {
        map[act] = { name: act, M: { realizadas: 0, pendientes: 0 }, F: { realizadas: 0, pendientes: 0 }, total: 0 };
      }
      const r = Number(item.actrealizadas || 0);
      const p = Number(item.actsinrealizar || 0);
      const sexo = String(item.sexo || '').toUpperCase();
      if (sexo === 'M') {
        map[act].M.realizadas += r;
        map[act].M.pendientes += p;
      } else {
        map[act].F.realizadas += r;
        map[act].F.pendientes += p;
      }
      map[act].total += r + p;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  };

  const aEdadesData = useMemo(
    () => processActividades(indicadoresData.actividadesGruposEdades),
    [indicadoresData],
  );
  const aCohorteData = useMemo(
    () => processActividades(indicadoresData.actividadesCohorte),
    [indicadoresData],
  );

  return {
    config,
    activeTab,
    setActiveTab: handleSetActiveTab,
    filtrosValores,
    catalogos,
    loadingConfig,
    loadingIndicadores,
    error,
    gruposEdadesChartData,
    gruposRiesgoData,
    totalRiesgo: gruposRiesgoData.reduce((a, b) => a + b.total, 0),
    totalPersonas: gruposEdadesChartData.reduce((a, b) => a + b.Total, 0),
    totalMujeres: gruposEdadesChartData.reduce((a, b) => a + b.F, 0),
    totalHombres: gruposEdadesChartData.reduce((a, b) => a + b.M, 0),
    aEdadesData,
    aCohorteData,
    handleFiltroChange,
    aplicarFiltros,
    limpiarFiltros,
  };
};

export default useDashboardData;
